import { useForm } from "@refinedev/antd";
import { HttpError, useCan, useCreate, useNavigation, useOne, useParsed, useUpdate } from "@refinedev/core";
import { Collapse, Form, Input, Spin } from "antd";
import { IntelligenceSearchList } from "./list";
import { PayloadConfigType, SaveConfigComponent } from "./component/crud/save-config-component";
import { TitleConfig } from "./component/crud/title-config";
import { IntelligenceFormFieldsEnum } from "./enum";
import { convertDateRangeToDayJS } from "@src/util/time-form";
import React from "react";
import { CrudCollapseStyle } from "@src/components/crud-collapse-style";
import { getUserInfo } from "@src/util/get-user-info";
import { ModeSave } from "./component/crud/content-modal-save-config";
import { Error403 } from "../error/403";
import { PAGE_URL } from "@src/config";
import { ButtonBackToListing } from "@src/components/crud-explore/button-back-to-listing";
import { CrudPermissionLayout, PERMISSION_LAYOUT_CHECK } from "@src/components/crud-permission-layout";

export const IntelligenceSearchEdit = () => {
    const parsed = useParsed();
    const { form } = useForm<any, HttpError, any>();
    const { mutateAsync, isLoading } = useUpdate();
    const { mutateAsync: mutateAsyncCreate } = useCreate();
    const { push } = useNavigation();

    const [initConfig, setInitConfig] = React.useState<Record<string, any>>({});

    const [dontHavePermission, setDontHavePermission] = React.useState(false);

    const {
        isLoading: isLoadingGet,
        isFetching: isFetchingGet,
        refetch,
    } = useOne({
        dataProviderName: "nestjsx",
        id: parsed?.id,
        resource: "explorations",
        queryOptions: {
            onSuccess: (data) => {
                const _config = data?.data?.config;
                if (!_config) return;
                const parsedTimeRange = _config?.[IntelligenceFormFieldsEnum.TIME_RANGE]
                    ? convertDateRangeToDayJS(_config?.[IntelligenceFormFieldsEnum.TIME_RANGE])
                    : undefined;
                const initConfig = {
                    isPublic: data?.data?.is_public,
                    owner: data?.data?.owner,
                    ..._config,

                    [IntelligenceFormFieldsEnum.TITLE]: data?.data?.title,
                    [IntelligenceFormFieldsEnum.DESCRIPTION]: data?.data?.description,
                    [IntelligenceFormFieldsEnum.TIME_RANGE]: parsedTimeRange,
                };
                const convertOrderFromOldVersionToNewVersion = (order: any) => {
                    /**
                     * new version
                     * {
                     *  name: string;
                     *  direction: "asc" | "desc";
                     * }
                     *
                     * old version
                     * {
                     *  field: string;
                     *  direction: "asc" | "desc";
                     *  meta: SmlAsset;
                     * }
                     */
                    if (order.field) {
                        return {
                            name: order.field,
                            direction: order.direction,
                        };
                    }
                    return order;
                };
                initConfig[IntelligenceFormFieldsEnum.QUERY_ORDER] = initConfig[
                    IntelligenceFormFieldsEnum.QUERY_ORDER
                ]?.map(convertOrderFromOldVersionToNewVersion);
                setInitConfig(initConfig);
                setTimeout(() => {
                    form.setFieldsValue(initConfig);
                }, 0);
            },
            onError: (err) => {
                if (err?.response?.status === 403) {
                    setDontHavePermission(true);
                }
            },
            queryKey: ["fetch-config", parsed.id],
        },
    });

    const productCode = initConfig?.[IntelligenceFormFieldsEnum.PRODUCT_CODE];
    const { data: dataCanEdit } = useCan({
        resource: "ab_test_exploration",
        action: productCode ? "create" : "list",
        params: {
            productCode,
        },
    });
    const isOwnerConfig = initConfig?.owner === getUserInfo()?.email;

    const handleUpdateExploration = async (
        payload: PayloadConfigType,
        cb?: () => void,
        optionMode?: string,
        forceUpdate?: boolean,
    ) => {
        const isUpdateExitConfig = forceUpdate || (optionMode === ModeSave && isOwnerConfig);

        if (isUpdateExitConfig) {
            await mutateAsync({
                resource: "explorations",
                id: parsed?.id,
                values: payload,
                dataProviderName: "nestjsx",
                successNotification: {
                    type: "success",
                    message: "Successfully saved",
                    description: "Your changes have been saved",
                },
                meta: {
                    headers: {
                        "product-code": payload.product_code,
                    },
                },
            });
            await refetch();
        } else {
            await mutateAsyncCreate({
                resource: "explorations",
                values: payload,
                dataProviderName: "nestjsx",
                successNotification: {
                    type: "success",
                    message: "Successfully created",
                    description: "Your explore have been created",
                },
                meta: {
                    headers: {
                        "product-code": payload.product_code,
                    },
                },
            }).then((data) => {
                window.open(`${window.location.origin}/explorations/intelligence-search/${data?.data?.id}`, "_blank");
            });
        }

        cb?.();
    };

    const isConfigLoading = isLoading || isFetchingGet || isLoadingGet;

    if (dontHavePermission) {
        return (
            <Error403
                message="You don't have permission to view this page. Please contact our admin for support
"
                onBack={() => {
                    push(PAGE_URL.EXPLORATION.ROOT);
                }}
            />
        );
    }

    return (
        <CrudCollapseStyle>
            <Spin spinning={isConfigLoading}>
                <CrudPermissionLayout isDisabled={!dataCanEdit?.can}>
                    <Collapse
                        expandIcon={() => <ButtonBackToListing />}
                        activeKey={["intelligence-search"]}
                        items={[
                            {
                                key: "intelligence-search",
                                label: (
                                    <div
                                        className={`mt-2 flex justify-between overflow-hidden ${PERMISSION_LAYOUT_CHECK}`}
                                    >
                                        <TitleConfig form={form} />
                                        <SaveConfigComponent
                                            form={form}
                                            isCreate={false}
                                            handleSave={handleUpdateExploration}
                                            isLoading={isConfigLoading}
                                            initConfig={initConfig}
                                            isOwnerConfig={isOwnerConfig}
                                        />
                                    </div>
                                ),
                                children: (
                                    <Form form={form} layout="vertical">
                                        <IntelligenceSearchList form={form} />
                                        <Form.Item name={IntelligenceFormFieldsEnum.TITLE} hidden>
                                            <Input />
                                        </Form.Item>
                                        <Form.Item name={IntelligenceFormFieldsEnum.DESCRIPTION} hidden>
                                            <Input />
                                        </Form.Item>
                                    </Form>
                                ),
                            },
                        ]}
                    />
                </CrudPermissionLayout>
            </Spin>
        </CrudCollapseStyle>
    );
};
